import React, { useState, useRef, useCallback, useEffect, FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import axios from 'axios';
import { Label } from '../../../components/ui/label';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { Checkbox } from '../../../components/ui/checkbox';
import { Eye, EyeOff, Mail, Lock, User } from 'lucide-react';
import { FcGoogle } from 'react-icons/fc';
import { useToast } from '../../../hooks/use-toast';
import { InternationalPhoneInput } from '../../../components/ui/international-phone-input';
import '../../../components/ui/international-phone-input.css';
import './SignUp.css';
// Use Vite's BASE_URL for robust public asset loading
import baseURL from '../../../http';
import { getDeviceUid } from '@/lib/deviceId';
import { useBranding, useBrandLogo, useBrandName } from '@/contexts/BrandingContext';
import { jwtDecode } from 'jwt-decode';
import { signInWithGoogle } from '../../public/Login/Firebase_Config';
import {
  ChooseContextDialog,
  NoAccessDialog,
  useCompleteSignIn,
} from '../../public/Login/ChooseContext';
import { readLinkReturn, rememberLinkReturn } from '../../public/Login/linkReturn';
import { isReturnablePath } from '@/features/site/accountLinks';
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
  getAuth,
  fetchSignInMethodsForEmail,
  User as FirebaseUser
} from "firebase/auth";

// ---------------- Validation helpers ----------------
type ValidationErrors = Partial<Record<"name"|"email"|"mobile"|"password"|"terms", string>>;
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Updated phone regex for international format (+country code + number)
const phoneRegex = /^\+[1-9]\d{7,14}$/;

const fbMessage = (code?: string) => {
  switch (code) {
    case "auth/email-already-in-use": return "This email is already registered.";
    case "auth/invalid-email": return "The email address is invalid.";
    case "auth/weak-password": return "Your password is too weak (min 6 characters).";
    case "auth/wrong-password": return "Incorrect password.";
    case "auth/user-not-found": return "No account found with this email.";
    case "auth/user-disabled": return "Your account has been disabled. Please contact support.";
    case "auth/network-request-failed": return "Network error. Please check your connection and try again.";
    case "auth/too-many-requests": return "Too many attempts. Please wait a bit before trying again.";
    default: return "Something went wrong. Please try again.";
  }
};

const mapBackendApiError = (err: any, fallback = "Something went wrong. Please try again.") => {
  if (axios.isAxiosError(err)) {
    if (err.response?.data?.message) return err.response.data.message;
    if (err.response?.data?.error) return err.response.data.error;
    if (err.response?.data?.detail) return err.response.data.detail;

    if (err.response?.status === 400) return "The request was invalid. Please try again.";
    if (err.response?.status === 401) return "Your Firebase session is invalid. Please try again.";
    if (err.response?.status === 403) return "Account access denied. Please contact support.";
    if (err.response?.status === 429) return "Too many attempts. Please wait before trying again.";
    if (err.response?.status && err.response.status >= 500) {
      return "Server error. Please try again later.";
    }

    if (err.code === "ECONNABORTED") {
      return "Request timeout. Please check your connection and try again.";
    }
    if (err.code === "ERR_NETWORK") {
      return "Network error. Please check your internet connection.";
    }
  }

  return fallback;
};

const validateFields = (data: any, termsAccepted: boolean) => {
  const e: ValidationErrors = {};
  if (!data.name?.trim()) e.name = "Name is required";
  if (!data.email?.trim()) e.email = "Email is required";
  else if (!emailRegex.test(data.email)) e.email = "Enter a valid email";
  if (!data.mobile?.trim()) e.mobile = "Phone Number is required";
  else if (!phoneRegex.test(data.mobile.replace(/\s/g, ''))) e.mobile = "Enter a valid international phone number";
  if (!data.password) e.password = "Password is required";
  else if (data.password.length < 6) e.password = "Password must be at least 6 characters";
  if (!termsAccepted) e.terms = "Please accept the Terms and Conditions";
  return e;
};

// resend verification throttle
const RESEND_MS = 60_000;
let LAST_RESEND_AT = 0;
const canResend = () => Date.now() - LAST_RESEND_AT >= RESEND_MS;

// Custom alert function using toast
const customAlert = (title: string, description: string, variant: "default" | "destructive" | "success") => {
  // This will be passed as parameter to use the toast hook
  return { title, description, variant };
};

const DEFAULT_REDIRECT_TO = "/admin_dashboard_page";

const sanitizeRedirectTarget = (
  target: string | null | undefined,
  fallback = DEFAULT_REDIRECT_TO
) => {
  if (!target) return fallback;

  try {
    if (target.startsWith("http://") || target.startsWith("https://")) {
      const url = new URL(target);
      if (url.origin !== window.location.origin) return fallback;
      target = `${url.pathname}${url.search}${url.hash}`;
    }
  } catch {
    return fallback;
  }

  if (!target.startsWith("/") || target.startsWith("//")) return fallback;
  if (target.startsWith("/login") || target.startsWith("/signup") || !isReturnablePath(target)) return fallback;
  return target;
};

const clearLegacyReturnStorage = () => {
  localStorage.removeItem("isRoutingManual");
  localStorage.removeItem("returnToInvitationUrl");
  localStorage.removeItem("returnToAssessmentUrl");
};

const SignUp: React.FC = () => {
  const [signupdata, setsignupdata] = useState({
    function_type: "Register",
    login_from: "", // Google, Email
    name: "",
    email: "",
    mobile: "",
    password: "",
    google_token: "",
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors_V, set_serrors_V] = useState<ValidationErrors>({});
  const [phoneValid, setPhoneValid] = useState(false);
  // Whose sign-up screen this is. Falls back to the EduExpertAI defaults on the
  // platform domain, so this renders as before there.
  const brandName = useBrandName();
  const { branding } = useBranding();
  const brandTagline = branding.tagline || '';
  const { logoUrl: brandLogo, fallbackUrl: platformLogo } = useBrandLogo();
  // A tenant logo is a remote URL that can 404 long after it was saved; drop to
  // the platform mark rather than to a blank square.
  const [brandLogoFailed, setBrandLogoFailed] = useState(false);
  // Same gate as the sign-in screen: Google alone on arrival, the email form
  // behind an explicit choice. A five-field form sitting beside a one-tap
  // button reads as the "real" way to register, and it is the slower, more
  // error-prone one.
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const { toast } = useToast();
  /** The target this page was given — router state, `?redirect=`, or the legacy keys. */
  const readExplicitReturnTo = useCallback((): string | null => {
    const stateReturnTo = (location.state as { returnTo?: string } | null)?.returnTo;
    const queryReturnTo = new URLSearchParams(location.search).get("redirect");
    const legacyReturnTo =
      localStorage.getItem("isRoutingManual") === "true"
        ? localStorage.getItem("returnToInvitationUrl") ||
          localStorage.getItem("returnToAssessmentUrl")
        : null;

    return stateReturnTo || queryReturnTo || legacyReturnTo || null;
  }, [location.search, location.state]);

  const getPostSignupRedirect = useCallback(
    // A link target remembered by an earlier page stands in when this one was
    // opened without any — see Login/linkReturn.
    () => sanitizeRedirectTarget(readExplicitReturnTo() || readLinkReturn()),
    [readExplicitReturnTo]
  );

  // Keep a link target this page was handed. A new account made from a link
  // verifies its email in a NEW tab, which has no URL or router state to carry
  // the link — the remembered copy is how that sign-in still finds its way back.
  useEffect(() => {
    const explicit = readExplicitReturnTo();
    if (explicit) rememberLinkReturn(sanitizeRedirectTarget(explicit));
  }, [readExplicitReturnTo]);

  // ---- After a Google sign-up: the same rule as every sign-in page ----
  // This path used to store the tokens and walk straight in, so an account
  // linked to no institute was stopped on /login and let in here. Now it gets
  // the same institute choice and the same "No access yet" — unless it is on
  // its way back to one of the shareable links. See useCompleteSignIn.
  const {
    completeSignIn,
    resumeExistingSession,
    noAccessDialogProps,
    contextDialogProps,
  } = useCompleteSignIn({
    getReturnTarget: getPostSignupRedirect,
    onFinish: (target, notice) => {
      if (notice) toast({ ...notice, duration: 2000 });
      clearLegacyReturnStorage();
      navigate(target, { replace: true });
    },
  });

  // ---- Authentication check on page load ----
  useEffect(() => {
    const checkExistingAuth = () => {
      try {
        const accessToken = localStorage.getItem('access_token') ;

        // A session already in storage continues, through the same no-access
        // rule as a fresh sign-in.
        if ( accessToken) {
              void resumeExistingSession();
              return;

        }

      } catch (error) {
        console.warn('Failed to check existing authentication:', error);
      }
    };

    // Check authentication on component mount
    checkExistingAuth();
    // document.title = "SignUp";
  }, [resumeExistingSession]);

  // Detect user's country based on locale
  const detectUserCountry = () => {
    try {
      const locale = navigator.language || 'en-US';
      const countryCode = locale.split('-')[1];

      const countryMap: { [key: string]: string } = {
        'US': 'US', 'CA': 'CA', 'GB': 'GB', 'AU': 'AU', 'IN': 'IN',
        'DE': 'DE', 'FR': 'FR', 'IT': 'IT', 'ES': 'ES', 'BR': 'BR',
        'MX': 'MX', 'AR': 'AR', 'JP': 'JP', 'KR': 'KR', 'CN': 'CN',
        'RU': 'RU', 'TR': 'TR', 'SA': 'SA', 'AE': 'AE', 'EG': 'EG',
        'ZA': 'ZA', 'NG': 'NG', 'KE': 'KE', 'PH': 'PH', 'TH': 'TH',
        'VN': 'VN', 'ID': 'ID', 'MY': 'MY', 'SG': 'SG', 'NZ': 'NZ'
      };

      return countryMap[countryCode] || 'IN';
    } catch {
      return 'IN';
    }
  };

  const defaultCountryCode = 'IN'; // Always default to India

  // Password strength calculation
  const getPasswordStrength = (password: string) => {
    if (password.length === 0) return 0;
    let strength = 0;
    if (password.length >= 6) strength++;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    return Math.min(strength, 4);
  };

  const passwordStrength = getPasswordStrength(signupdata.password);
  const getStrengthLabel = (strength: number) => {
    switch (strength) {
      case 0: return '';
      case 1: return 'Very Weak';
      case 2: return 'Weak';
      case 3: return 'Good';
      case 4: return 'Strong';
      default: return '';
    }
  };

  const getStrengthColor = (strength: number) => {
    switch (strength) {
      case 1: return '#ef4444';
      case 2: return '#f59e0b';
      case 3: return '#10b981';
      case 4: return '#059669';
      default: return '#e5e7eb';
    }
  };

  // Enhanced custom alert function that uses toast
  const showAlert = (title: string, description: string, variant: "default" | "destructive" = "default") => {
    toast({
      title,
      description,
      variant,
      duration: 5000,
    });
  };

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    setsignupdata(prev => ({ ...prev, [name]: value }));

    // Clear specific field error when user starts typing
    if (errors_V[name as keyof ValidationErrors]) {
      set_serrors_V(prev => ({ ...prev, [name]: undefined }));
    }
  }, [errors_V]);

  // Handle international phone number change
  const handlePhoneChange = useCallback((value: string, country: any) => {
    setsignupdata(prev => ({ ...prev, mobile: value }));
    setSelectedCountry(country);

    // Clear mobile error when user starts typing
    if (errors_V.mobile) {
      set_serrors_V(prev => ({ ...prev, mobile: undefined }));
    }
  }, [errors_V.mobile]);

  // Handle phone validation callback
  const handlePhoneValidation = useCallback((isValid: boolean) => {
    setPhoneValid(isValid);
  }, []);

  const handleCheckboxChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setAcceptTerms(event.target.checked);
    // Clear terms error when checkbox is checked
    if (event.target.checked && errors_V.terms) {
      set_serrors_V(prev => ({ ...prev, terms: undefined }));
    }
  }, [errors_V.terms]);

  const handleClickShowPassword = useCallback(() => {
    setShowPassword(show => !show);
  }, []);

  const handleMouseDownPassword = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  }, []);

  const handleOpenDialog = () => setDialogOpen(true);
  const handleCloseDialog = () => setDialogOpen(false);

  // useEffect(() => {
  //   const accessToken = localStorage.getItem('access_token');
  //   const refreshToken = localStorage.getItem('refresh_token');
  //   if (accessToken && refreshToken) {
  //     window.location.href = "/";
  //     return;
  //   }
  //   document.title = "SignUp";
  // }, []);

  const upsertBackend = async (firebaseToken: string, data: any) => {
    return axios.post(`${baseURL}/public/SignUpEmail/`, {
      firebase_token: firebaseToken,
      function_type: "Register",
      login_from: "Email",
      name: data.name.trim(),
      email: data.email.trim(),
      mobile: data.mobile.trim(),
      country_code: selectedCountry?.code || null,
      country_name: selectedCountry?.name || null,
    });
  };

  const resendVerification = async (user: FirebaseUser) => {
    if (!canResend()) {
      const secs = Math.ceil((RESEND_MS - (Date.now() - LAST_RESEND_AT)) / 1000);
      return { ok: false as const, error: `Please wait ${secs}s before trying again.` };
    }
    try {
      await sendEmailVerification(user);
      LAST_RESEND_AT = Date.now();
      return { ok: true as const };
    } catch (e: any) {
      const msg = e?.code === "auth/too-many-requests"
        ? "Too many attempts. Please wait a bit before trying again."
        : e?.message || "Failed to send verification email.";
      return { ok: false as const, error: msg };
    }
  };

  const getDeviceType = () => {
    const ua = navigator.userAgent;
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) return "Web Tablet";
    if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) return "Web Mobile";
    return "Web Desktop";
  };

  // ---------------- Email Sign Up ----------------
  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    set_serrors_V({});

    const v = validateFields(signupdata, acceptTerms);
    if (Object.keys(v).length > 0) {
      set_serrors_V(v);
      showAlert("Error", "Please fill in all required fields correctly.", "destructive");
      setLoading(false);
      return;
    }

    const authInst = getAuth();

    try {
      // 1) First, check if email already exists
      const emailTrimmed = signupdata.email.trim();
      const signInMethods = await fetchSignInMethodsForEmail(authInst, emailTrimmed);

      if (signInMethods.length > 0) {
        // Email already exists - show proper error message and stop
        const errorMessage = "This email address is already registered. Please use a different email or sign in with your existing account.";
        set_serrors_V({ email: "Email already registered" });
        toast({
          title: "Email Already Registered",
          description: errorMessage,
          variant: "destructive",
          duration: 5000,
        });
        setLoading(false);
        return;
      }

      // 2) Email doesn't exist, proceed with account creation
      const cred = await createUserWithEmailAndPassword(authInst, emailTrimmed, signupdata.password);

      // 3) Send verification (non-fatal if fails)
      let verificationOk = true;
      try {
        await sendEmailVerification(cred.user);
        LAST_RESEND_AT = Date.now();
      } catch {
        verificationOk = false;
      }

      const firebaseToken = await cred.user.getIdToken();

      // 4) Upsert backend
      try {
        await upsertBackend(firebaseToken, signupdata);
      } catch (apiErr: any) {
        if (apiErr?.response?.status !== 409) throw apiErr; // treat 409 as linked
      }

      // 5) Success messages
      if (verificationOk) {
        showAlert("Success", `Account created successfully! We sent a verification email to ${emailTrimmed}. Please verify, then sign in.`);
      } else {
        showAlert("Warning", "Account created, but we couldn't send the verification email. You can resend it from the login page.");
      }
      const redirectTo = getPostSignupRedirect();
      clearLegacyReturnStorage();
      navigate("/login", { state: { returnTo: redirectTo } });
    } catch (err: any) {
      // Handle other Firebase errors
      if (err?.code === "auth/email-already-in-use") {
        // Fallback - though we should have caught this earlier
        const errorMessage = "This email address is already registered. Please use a different email or sign in with your existing account.";
        set_serrors_V({ email: "Email already registered" });
        toast({
          title: "Email Already Registered",
          description: errorMessage,
          variant: "destructive",
          duration: 5000,
        });
      } else {
        if (axios.isAxiosError(err)) {
          toast({
            title: "Error",
            description: mapBackendApiError(err),
            variant: "destructive",
            duration: 5000,
          });
          return;
        }
        toast({
          title: "Error",
          description: fbMessage(err?.code),
          variant: "destructive",
          duration: 5000,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // ---------------- Google Sign Up ----------------
  const handleGoogleLogin = useCallback(async () => {
    setLoading(true);
    try {
      const { token, result } = await signInWithGoogle();
      if (!token || !result?.user?.email) {
        showAlert("Error", "Google Sign-in failed. Please try again.", "destructive");
        return;
      }

      const updatedSignupData = {
        ...signupdata,
        function_type: "Register",
        login_from: "Google",
        name: result.user.displayName ?? "",
        email: result.user.email ?? "",
        google_token: token,
      };

      const response = await axios.post(`${baseURL}/public/google-auth/`, updatedSignupData, {
        headers: { 'X-Device-Type': getDeviceType(), 'X-Device-Id': getDeviceUid() }
      });
      if (response.status === 200) {
        const userData = response.data || {};

        // An existing account with 2FA gets no tokens here, only a code by
        // email. Going on to the target without tokens bounced it straight back
        // to /login; send it where Login sends it, carrying the target along.
        if (userData["2fa_required"] === true) {
          if (userData.user_id) localStorage.setItem("2fa_user_id", userData.user_id.toString());
          if (userData.session_id) localStorage.setItem("2fa_session_id", userData.session_id);
          localStorage.setItem("2fa_timestamp", Date.now().toString());
          toast({
            title: "2FA Required",
            description: "Please enter the verification code sent to your email.",
          });
          navigate(`/2fa?redirect=${encodeURIComponent(getPostSignupRedirect())}`);
          return;
        }

        if (userData.access) {
            localStorage.setItem("access_token", userData.access);
            localStorage.setItem("refresh_token", userData.refresh);
            const decoded: any = jwtDecode(userData.access);
            localStorage.setItem("token_expiration", (decoded.exp * 1000).toString());
        }

        const message = userData.function_type === "Register" ? "Registered Successfully!" : "Signed in Successfully!";
        await completeSignIn(userData.email ?? result.user.email ?? null, {
          title: "Success! 🎉",
          description: message,
        });
      } else {
        showAlert("Error", "Google Sign-in failed. Please try again.", "destructive");
      }
    } catch (err: any) {
      showAlert(
        "Error",
        mapBackendApiError(err, "Google Sign-in failed. Please try again."),
        "destructive"
      );
    } finally {
      setLoading(false);
    }
  }, [signupdata, getPostSignupRedirect, navigate, completeSignIn]);

  const isButtonDisabled = !acceptTerms || loading;

  return (
    <div className="signup-container">
      <div className="signup-content">
        {/* Header */}
        <div className="signup-header">
          <div className="signup-logo-section">
            <div className="signup-logo-row">
              <div className="signup-logo-icon">
                <img
                  src={brandLogoFailed ? platformLogo : brandLogo}
                  alt={brandName}
                  onError={() => setBrandLogoFailed(true)}
                />
              </div>
              <h1 className="signup-logo-text">{brandName}</h1>
            </div>
          {brandTagline ? (
            <p className="text-sm text-muted-foreground mt-1">{brandTagline}</p>
          ) : null}
          </div>
        </div>

        <Card className="signup-card">
          <CardHeader className="signup-card-header">
            <CardTitle className="signup-card-title">
              {showEmailForm ? 'Sign up with email' : 'Create Account'}
            </CardTitle>
            {showEmailForm ? (
              <CardDescription>Fill in your details below</CardDescription>
            ) : null}
          </CardHeader>

          <CardContent className="signup-card-content">
            {/* Google — always on the card, in the same place on both views,
                so revealing the email form never moves it. Signing in with
                Google creates the account on first use, so for most people
                this button IS the whole sign-up. */}
            <div className="signup-primary-auth">
              <Button
                variant="outline"
                className="signup-google-btn"
                onClick={handleGoogleLogin}
                disabled={loading}
              >
                {loading ? (
                  <div className="signup-google-spinner" />
                ) : (
                  <FcGoogle className="signup-google-icon" />
                )}
                Continue with Google
              </Button>

              {!showEmailForm && (
                <button
                  type="button"
                  className="signup-alt-auth-link"
                  onClick={() => {
                    set_serrors_V({});
                    setShowEmailForm(true);
                  }}
                  disabled={loading}
                >
                  Sign up with email
                </button>
              )}
            </div>

            {showEmailForm && (
            <div className="signup-email-section">
            {/* Enhanced Sign Up Form */}
            <form onSubmit={handleSubmit} className="signup-form">
              {/* Name + Email side by side */}
              <div className="signup-row-2col">
                <div className="signup-field">
                  <Label htmlFor="name" className="signup-label">
                    Full Name *
                  </Label>
                  <div className="signup-input-wrapper">
                    <User className="signup-input-icon" />
                    <Input
                      id="name"
                      name="name"
                      placeholder="Your full name"
                      value={signupdata.name}
                      onChange={handleChange}
                      className={`signup-input ${errors_V.name ? 'error' : ''}`}
                      required
                    />
                  </div>
                  {errors_V.name && (
                    <span className="error-message">{errors_V.name}</span>
                  )}
                </div>

                <div className="signup-field">
                  <Label htmlFor="email" className="signup-label">
                    Email Address *
                  </Label>
                  <div className="signup-input-wrapper">
                    <Mail className="signup-input-icon" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="Your email"
                      value={signupdata.email}
                      onChange={handleChange}
                      className={`signup-input ${errors_V.email ? 'error' : ''}`}
                      required
                    />
                  </div>
                  {errors_V.email && (
                    <span className="error-message">{errors_V.email}</span>
                  )}
                </div>
              </div>

              {/* Mobile Phone */}
              <div className="signup-field">
                <Label htmlFor="mobile" className="signup-label">
                  Phone Number *
                </Label>
                <InternationalPhoneInput
                  value={signupdata.mobile}
                  onChange={handlePhoneChange}
                  onValidation={handlePhoneValidation}
                  placeholder="Enter your phone number"
                  error={!!errors_V.mobile}
                  className={errors_V.mobile ? 'error' : ''}
                  defaultCountry={defaultCountryCode}
                />
                {errors_V.mobile && (
                  <span className="error-message">{errors_V.mobile}</span>
                )}
                {phoneValid && signupdata.mobile && (
                  <div className="phone-validation-success">
                    <span className="success-message">✓ Valid phone number</span>
                  </div>
                )}
              </div>

              {/* Password */}
              <div className="signup-field">
                <Label htmlFor="password" className="signup-label">
                  Password *
                </Label>
                <div className="signup-input-wrapper">
                  <Lock className="signup-input-icon" />
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Create a password (min 6 characters)"
                    value={signupdata.password}
                    onChange={handleChange}
                    className={`signup-input signup-password-input ${errors_V.password ? 'error' : ''}`}
                    required
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="signup-password-toggle"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                  >
                    {showPassword ? (
                      <EyeOff className="signup-toggle-icon" />
                    ) : (
                      <Eye className="signup-toggle-icon" />
                    )}
                  </Button>
                </div>
                {errors_V.password && (
                  <span className="error-message">{errors_V.password}</span>
                )}
                {signupdata.password && (
                  <div className="password-strength">
                    <div className="password-strength-indicator">
                      <div className="password-strength-bars">
                        {[1, 2, 3, 4].map((level) => (
                          <div
                            key={level}
                            className="password-strength-bar"
                            style={{
                              backgroundColor: level <= passwordStrength ? getStrengthColor(passwordStrength) : '#e5e7eb'
                            }}
                          />
                        ))}
                      </div>
                      <span
                        className="password-strength-label"
                        style={{ color: getStrengthColor(passwordStrength) }}
                      >
                        {getStrengthLabel(passwordStrength)}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Terms Agreement */}
              <div className="signup-terms-wrapper">
                <Checkbox
                  id="acceptTerms"
                  checked={acceptTerms}
                  onCheckedChange={(checked) => {
                    setAcceptTerms(checked as boolean);
                    handleCheckboxChange({ target: { checked: checked as boolean } } as any);
                  }}
                  className={`signup-terms-checkbox ${errors_V.terms ? 'error' : ''}`}
                />
                <Label htmlFor="acceptTerms" className="signup-terms-label">
                  I agree to the{' '}
                  <a
                    href="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="signup-terms-link"
                  >
                    Terms and Conditions
                  </a>{' '}
                  and{' '}
                  <a
                    href="/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="signup-terms-link"
                  >
                    Privacy Policy
                  </a>
                </Label>
              </div>
              {errors_V.terms && (
                <span className="error-message">{errors_V.terms}</span>
              )}

              {/* Submit Button */}
              <Button 
                type="submit" 
                className="signup-submit-btn" 
                disabled={isButtonDisabled}
              >
                {loading ? (
                  <>
                    <div className="signup-submit-spinner" />
                    Creating Account...
                  </>
                ) : (
                  'Create Account'
                )}
              </Button>
            </form>
            </div>
            )}
          </CardContent>

          <CardFooter className="signup-footer">
            <div className="signup-signin-text">
              Already have an account?{' '}
              <Link
                to="/login"
                state={{ returnTo: getPostSignupRedirect() }}
                className="signup-signin-link"
              >
                Sign in here
              </Link>
            </div>

            {/* The form has its own explicit consent checkbox, but the Google
                view creates an account with one tap and had no consent text at
                all — so this line belongs on both. */}
            <p className="signup-legal-text">
              By continuing you agree to our{' '}
              <a
                href="/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="signup-legal-link"
              >
                Terms &amp; Conditions
              </a>{' '}
              and{' '}
              <a
                href="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="signup-legal-link"
              >
                Privacy Policy
              </a>
              .
            </p>
          </CardFooter>
        </Card>

      </div>

      <NoAccessDialog {...noAccessDialogProps} />

      <ChooseContextDialog {...contextDialogProps} />
    </div>
  );
};

export default SignUp;
